import { Injectable } from '@angular/core';
import { StorageEngine } from './storage-engine.interface';
import { BrowserStorageService } from './browser-storage.service';
import { ServerStorageService } from './server-storage.service';
import { PlatformService } from '../platform.service';

/**
 * Bump when persisted data can no longer be read by the current build.
 * v2 — int → UUID v7 primary keys: every id persisted before the cutover (account/organization ids, the active
 * project, last boards, cached static catalogs, JWTs with int claims, saved list filters) is meaningless now.
 */
export const STORAGE_SCHEMA_VERSION = '2';
export const STORAGE_SCHEMA_VERSION_KEY = 'storageSchemaVersion';

/** localStorage keys that hold entity ids and are dropped on a schema bump (the user signs in again). */
const ID_BEARING_KEYS = [
  'accessToken',
  'refreshToken',
  'accountId',
  'group',
  'userData',
  'userSettings',
  'passwordChangeRequired',
  'organizations',
  'active_project',
  'staticCatalogs',
  'staticCatalogsHash',
  'notificationDecision',
  'crossTabAuthState',
];
/** localStorage key prefixes dropped on a schema bump. */
const ID_BEARING_PREFIXES = ['project_last_board_'];
/** sessionStorage key prefixes dropped on a schema bump (saved list filters may hold ids). */
const SESSION_ID_BEARING_PREFIXES = ['entity_filters_'];

@Injectable({ providedIn: 'root' })
export class StorageService implements StorageEngine {
  private engine: StorageEngine;

  constructor(
    private platformService: PlatformService,
    browserStorage: BrowserStorageService,
    serverStorage: ServerStorageService,
  ) {
    this.engine = this.platformService.isBrowser() ? browserStorage : serverStorage;
    if (this.platformService.isBrowser()) {
      this.migrateSchema();
    }
  }

  /** Runs before any other service reads storage (they all inject this one). */
  private migrateSchema(): void {
    try {
      if (this.engine.getItem(STORAGE_SCHEMA_VERSION_KEY) === STORAGE_SCHEMA_VERSION) {
        return;
      }
      ID_BEARING_KEYS.forEach((key) => this.engine.removeItem(key));
      this.removeByPrefix(localStorage, ID_BEARING_PREFIXES);
      this.removeByPrefix(sessionStorage, SESSION_ID_BEARING_PREFIXES);
      this.engine.setItem(STORAGE_SCHEMA_VERSION_KEY, STORAGE_SCHEMA_VERSION);
    } catch (error) {
      console.warn('Storage schema migration failed:', error);
    }
  }

  private removeByPrefix(storage: Storage, prefixes: string[]): void {
    const keys: string[] = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (key && prefixes.some((prefix) => key.startsWith(prefix))) {
        keys.push(key);
      }
    }
    keys.forEach((key) => storage.removeItem(key));
  }

  getItem(key: string): string | null {
    return this.engine.getItem(key);
  }

  setItem(key: string, value: string): void {
    this.engine.setItem(key, value);
  }

  removeItem(key: string): void {
    this.engine.removeItem(key);
  }

  clear(): void {
    this.engine.clear();
  }

  /**
   * Set user's notification decision
   * @param userId - User ID to associate the decision with
   * @param decision - User's decision about notifications ('allowed' | 'denied')
   */
  setUserNotificationDecision(userId: string, decision: 'allowed' | 'denied'): void {
    const key = 'notificationDecision';
    const existingData = this.getItem(key);
    let notificationDecisions: Record<string, 'allowed' | 'denied'> = {};

    if (existingData) {
      try {
        notificationDecisions = JSON.parse(existingData);
      } catch (error) {
        console.warn('Error parsing existing notification decisions:', error);
        notificationDecisions = {};
      }
    }

    notificationDecisions[userId] = decision;
    this.setItem(key, JSON.stringify(notificationDecisions));
  }

  /**
   * Get user's notification decision
   * @param userId - User ID to get the decision for
   * @returns User's notification decision or null if not set
   */
  getUserNotificationDecision(userId: string): 'allowed' | 'denied' | null {
    const key = 'notificationDecision';
    const data = this.getItem(key);

    if (!data) {
      return null;
    }

    try {
      const notificationDecisions: Record<string, 'allowed' | 'denied'> = JSON.parse(data);
      return notificationDecisions[userId] || null;
    } catch (error) {
      console.warn('Error parsing notification decisions:', error);
      return null;
    }
  }

  /**
   * Remove user's notification decision
   * @param userId - User ID to remove the decision for
   */
  removeUserNotificationDecision(userId: string): void {
    const key = 'notificationDecision';
    const data = this.getItem(key);

    if (!data) {
      return;
    }

    try {
      const notificationDecisions: Record<string, 'allowed' | 'denied'> = JSON.parse(data);
      delete notificationDecisions[userId];

      if (Object.keys(notificationDecisions).length === 0) {
        this.removeItem(key);
      } else {
        this.setItem(key, JSON.stringify(notificationDecisions));
      }
    } catch (error) {
      console.warn('Error parsing notification decisions for removal:', error);
    }
  }
}

